/**
 * Per-worker labour detail slide-over.
 *
 * Mirrors mobile's labour detail screen
 *   /Users/gudupuramesh/Documents/siteexpens/app/(app)/projects/[id]/attendance/[recordId].tsx
 *
 * Sections (top → bottom):
 *   1. Hero — avatar + name + role + ₹ rate/unit
 *   2. Lifetime stats strip — total days · present · half · absent ·
 *      payable units · estimated payout
 *   3. Monthly breakdown — one card per month (newest first), each with:
 *      - Per-month roll-up (present / half / absent / units / hours / ₹)
 *      - Per-day list (most-recent first) with date, weekday, status pill,
 *        and the snapshot pay rate if it changed across months
 *   4. Footer — Edit button (opens `LabourFormSlideOver`)
 *
 * Data:
 *   - `useLabourAttendance(orgId, projectId, labourId)` for the full
 *     history. Same client-side filter pattern mobile uses.
 *
 * Wage math (matches mobile `dayPayEstimate`):
 *   units    = present × 1 + half × 0.5
 *   hours    = present × 8 + half × 4
 *   payable  = sum over each record of (rate × units) for day-rate,
 *              or (rate × hours) for hour-rate workers.
 */
import { useEffect, useMemo, useState } from 'react';

import { LabourFormSlideOver } from '@/components/LabourFormSlideOver';
import {
  STATUS_LABEL,
  avatarTintFor,
  initialOf,
  type AttendanceRecord,
  type AttendanceUiStatus,
  type ProjectLabour,
} from '@/lib/attendance';
import { useLabourAttendance } from '@/lib/useLabourAttendance';

// ───── Pure helpers ─────

function statusUnits(s: AttendanceUiStatus): number {
  if (s === 'present') return 1;
  if (s === 'half_day') return 0.5;
  return 0;
}
function statusHours(s: AttendanceUiStatus): number {
  if (s === 'present') return 8;
  if (s === 'half_day') return 4;
  return 0;
}

function monthKey(dateKey: string): string {
  return dateKey.slice(0, 7); // YYYY-MM
}

function monthLabel(key: string): string {
  const [y, m] = key.split('-').map((x) => parseInt(x, 10));
  if (!y || !m) return key;
  return new Date(y, m - 1, 1).toLocaleDateString('en-IN', {
    month: 'long',
    year: 'numeric',
  });
}

function fmtDayLong(dateKey: string): string {
  const [y, m, d] = dateKey.split('-').map((x) => parseInt(x, 10));
  if (!y || !m || !d) return dateKey;
  return new Date(y, m - 1, d).toLocaleDateString('en-IN', {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
  });
}

type Totals = {
  present: number;
  half: number;
  absent: number;
  units: number;
  hours: number;
  payable: number;
  days: number;
};

function summarize(rows: AttendanceRecord[]): Totals {
  let present = 0;
  let half = 0;
  let absent = 0;
  let units = 0;
  let hours = 0;
  let payable = 0;
  for (const r of rows) {
    if (r.status === 'present') present++;
    else if (r.status === 'half_day') half++;
    else if (r.status === 'absent') absent++;
    const u = statusUnits(r.status as AttendanceUiStatus);
    const h = statusHours(r.status as AttendanceUiStatus);
    units += u;
    hours += h;
    if (r.payRate && r.payRate > 0) {
      if (r.payUnit === 'hour') payable += r.payRate * h;
      else payable += r.payRate * u;
    }
  }
  return { present, half, absent, units, hours, payable, days: rows.length };
}

// ───── Component ─────

export function LabourDetailSlideOver({
  open,
  onClose,
  orgId,
  projectId,
  labour,
}: {
  open: boolean;
  onClose: () => void;
  orgId: string | null;
  projectId: string;
  labour: ProjectLabour | null;
}) {
  const { data, loading } = useLabourAttendance(
    orgId,
    projectId,
    labour?.labourId,
  );
  const [editOpen, setEditOpen] = useState(false);

  // Body scroll lock + Esc to close.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !editOpen) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose, editOpen]);

  const lifetime = useMemo(() => summarize(data), [data]);

  const months = useMemo(() => {
    if (data.length === 0) return [] as { key: string; label: string; totals: Totals; days: AttendanceRecord[] }[];
    const buckets = new Map<string, AttendanceRecord[]>();
    for (const r of data) {
      const k = monthKey(r.date);
      if (!k) continue;
      const arr = buckets.get(k) ?? [];
      arr.push(r);
      buckets.set(k, arr);
    }
    return Array.from(buckets.entries())
      .map(([k, rows]) => ({
        key: k,
        label: monthLabel(k),
        totals: summarize(rows),
        // Newest day first within each month.
        days: [...rows].sort((a, b) => b.date.localeCompare(a.date)),
      }))
      .sort((a, b) => b.key.localeCompare(a.key));
  }, [data]);

  const tint = labour ? avatarTintFor(labour.labourName) : { bg: '#F3F4F6', fg: '#6B7280' };

  return (
    <>
      <div
        className={`slide-overlay${open ? ' open' : ''}`}
        onClick={onClose}
      />
      <div className={`slide-over${open ? ' open' : ''}`}>
        <div className="slide-header">
          <h2 className="slide-title">
            <i className="ph ph-hard-hat" /> Worker Detail
          </h2>
          <button
            type="button"
            className="slide-close"
            onClick={onClose}
            aria-label="Close"
          >
            <i className="ph ph-x" />
          </button>
        </div>

        <div className="slide-body">
          {!labour ? (
            <div style={{ textAlign: 'center', padding: 32, color: 'var(--text-tertiary)' }}>
              Worker not found.
            </div>
          ) : (
            <>
              {/* ─── Hero ─── */}
              <div
                className="card"
                style={{
                  padding: 16,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 14,
                }}
              >
                <div
                  className="att-avatar"
                  style={{
                    width: 56,
                    height: 56,
                    fontSize: 22,
                    background: tint.bg,
                    color: tint.fg,
                  }}
                >
                  {initialOf(labour.labourName)}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 17,
                      fontWeight: 700,
                      color: 'var(--text-primary)',
                    }}
                  >
                    {labour.labourName}
                  </div>
                  <div
                    style={{
                      fontSize: 13,
                      color: 'var(--text-secondary)',
                      marginTop: 2,
                    }}
                  >
                    {labour.labourRole}
                    {typeof labour.payRate === 'number' && labour.payRate > 0 ? (
                      <>
                        {' · '}₹{labour.payRate.toLocaleString('en-IN')}/
                        {labour.payUnit === 'hour' ? 'hr' : 'day'}
                      </>
                    ) : null}
                  </div>
                  {labour.description ? (
                    <div
                      style={{
                        fontSize: 12,
                        color: 'var(--text-tertiary)',
                        marginTop: 4,
                        lineHeight: 1.4,
                      }}
                    >
                      {labour.description}
                    </div>
                  ) : null}
                </div>
                <button
                  type="button"
                  className="btn-secondary"
                  style={{ height: 32, fontSize: 12 }}
                  onClick={() => setEditOpen(true)}
                  title="Edit worker"
                >
                  <i className="ph ph-pencil-simple" /> Edit
                </button>
              </div>

              {/* ─── Lifetime stat strip ─── */}
              <div>
                <SectionTitle>Lifetime totals</SectionTitle>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    gap: 8,
                  }}
                >
                  <StatBox label="Days marked" value={String(lifetime.days)} />
                  <StatBox
                    label="Present"
                    value={String(lifetime.present)}
                    tint="success"
                  />
                  <StatBox
                    label="Half"
                    value={String(lifetime.half)}
                    tint="warning"
                  />
                  <StatBox
                    label="Absent"
                    value={String(lifetime.absent)}
                    tint="critical"
                  />
                  <StatBox
                    label="Payable units"
                    value={lifetime.units.toFixed(1)}
                  />
                  <StatBox
                    label="Est. payout"
                    value={`₹${Math.round(lifetime.payable).toLocaleString('en-IN')}`}
                  />
                </div>
              </div>

              {/* ─── Monthly breakdown ─── */}
              <div>
                <SectionTitle>Monthly breakdown</SectionTitle>
                {loading ? (
                  <div
                    style={{
                      padding: 32,
                      textAlign: 'center',
                      color: 'var(--text-tertiary)',
                      fontSize: 13,
                    }}
                  >
                    Loading attendance history…
                  </div>
                ) : months.length === 0 ? (
                  <div
                    style={{
                      padding: 32,
                      textAlign: 'center',
                      fontSize: 13,
                      color: 'var(--text-tertiary)',
                      border: '1px dashed var(--border)',
                      borderRadius: 8,
                    }}
                  >
                    No attendance marked yet for this worker.
                  </div>
                ) : (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 12,
                    }}
                  >
                    {months.map((m) => (
                      <MonthCard key={m.key} month={m} />
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Edit form — opens on top of the detail slide-over */}
      <LabourFormSlideOver
        open={editOpen}
        onClose={() => setEditOpen(false)}
        orgId={orgId}
        projectId={projectId}
        labour={labour}
      />
    </>
  );
}

// ───── Sub-components ─────

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontSize: 11,
        fontWeight: 700,
        color: 'var(--text-tertiary)',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        marginBottom: 8,
        paddingBottom: 6,
        borderBottom: '1px solid var(--border)',
      }}
    >
      {children}
    </div>
  );
}

function StatBox({
  label,
  value,
  tint,
}: {
  label: string;
  value: string;
  tint?: 'success' | 'warning' | 'critical';
}) {
  const bg =
    tint === 'success'
      ? 'var(--success-bg)'
      : tint === 'warning'
        ? 'var(--warning-bg)'
        : tint === 'critical'
          ? 'var(--critical-bg)'
          : '#FAFAFA';
  const fg =
    tint === 'success'
      ? 'var(--success-text)'
      : tint === 'warning'
        ? 'var(--warning-text)'
        : tint === 'critical'
          ? 'var(--critical-text)'
          : 'var(--text-primary)';
  return (
    <div
      style={{
        background: bg,
        border: '1px solid var(--border)',
        borderRadius: 8,
        padding: 12,
      }}
    >
      <div
        style={{
          fontSize: 20,
          fontWeight: 700,
          color: fg,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </div>
      <div
        style={{
          fontSize: 10,
          fontWeight: 700,
          color: 'var(--text-tertiary)',
          textTransform: 'uppercase',
          letterSpacing: '0.4px',
          marginTop: 4,
        }}
      >
        {label}
      </div>
    </div>
  );
}

function MonthCard({
  month,
}: {
  month: { key: string; label: string; totals: Totals; days: AttendanceRecord[] };
}) {
  const [open, setOpen] = useState(true);
  const t = month.totals;
  return (
    <div
      style={{
        border: '1px solid var(--border)',
        borderRadius: 8,
        overflow: 'hidden',
        background: '#fff',
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{
          width: '100%',
          padding: '12px 16px',
          background: '#F9FAFB',
          borderBottom: open ? '1px solid var(--border)' : 'none',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          cursor: 'pointer',
          fontFamily: 'inherit',
          border: 'none',
          textAlign: 'left',
        }}
      >
        <div>
          <div
            style={{
              fontSize: 13,
              fontWeight: 700,
              color: 'var(--text-primary)',
            }}
          >
            {month.label}
          </div>
          <div
            style={{
              fontSize: 11,
              color: 'var(--text-secondary)',
              marginTop: 2,
              display: 'flex',
              gap: 10,
              flexWrap: 'wrap',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            <span>
              <b style={{ color: 'var(--success-text)' }}>{t.present}</b> P
            </span>
            <span>
              <b style={{ color: 'var(--warning-text)' }}>{t.half}</b> H
            </span>
            <span>
              <b style={{ color: 'var(--critical-text)' }}>{t.absent}</b> A
            </span>
            <span style={{ color: 'var(--text-tertiary)' }}>·</span>
            <span>
              {t.units.toFixed(1)} units · {t.hours} hrs
            </span>
            {t.payable > 0 ? (
              <>
                <span style={{ color: 'var(--text-tertiary)' }}>·</span>
                <span style={{ color: 'var(--brand)', fontWeight: 600 }}>
                  ₹{Math.round(t.payable).toLocaleString('en-IN')}
                </span>
              </>
            ) : null}
          </div>
        </div>
        <i
          className={`ph-bold ${open ? 'ph-caret-up' : 'ph-caret-down'}`}
          style={{ color: 'var(--text-tertiary)' }}
        />
      </button>

      {open ? (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {month.days.map((d) => {
            const cls =
              d.status === 'present'
                ? 'present'
                : d.status === 'half_day'
                  ? 'half'
                  : d.status === 'absent'
                    ? 'absent'
                    : 'unmarked';
            const ratePart =
              d.payRate && d.payRate > 0
                ? ` · ₹${d.payRate.toLocaleString('en-IN')}/${d.payUnit === 'hour' ? 'hr' : 'day'}`
                : '';
            return (
              <div
                key={d.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '10px 16px',
                  borderTop: '1px solid var(--border)',
                  fontSize: 13,
                }}
              >
                <div>
                  <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                    {fmtDayLong(d.date)}
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      color: 'var(--text-tertiary)',
                      marginTop: 2,
                    }}
                  >
                    {d.date}
                    {ratePart}
                  </div>
                </div>
                <span className={`att-status-pill ${cls}`}>
                  {STATUS_LABEL[d.status as AttendanceUiStatus]}
                </span>
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
